import { createContext, useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import userService from '../services/userService';
import constants from '../utils/constants';
import helpers from '../utils/helpers';
import toast from 'react-hot-toast';
import commonService from '../services/commonService';
import { THEMES } from '../constants';

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: {
    permissions: {},
  },
  role: constants.role.buyer,
  theme: localStorage.getItem('theme') || THEMES.LIGHT
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, role } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      role
    };
  },
  LOGIN: (state, action) => {
    const { user, role } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      role
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: false,
      user
    };
  },
  ROLE: (state, action) => {
    const { role } = action.payload;

    return {
      ...state,
      role,
    };
  },
  CHANGETHEME: (state, action) => {
    const { theme } = action.payload;
    localStorage.setItem('theme', theme);
    return {
      ...state,
      theme,
    };
  }
};

const reducer = (state, action) => (handlers[action.type]
  ? handlers[action.type](state, action)
  : state);

const AuthContext = createContext({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  passwordRecovery: () => Promise.resolve(),
  passwordReset: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [favSellerGroup, setFavSellerGroup] = useState([]);
  const [contentSearchOpen, setContentSearchOpen] = useState(false);

  const setClassToBody = () => {
    const { body } = document;
    const className = {
      buyer: 'tp-customer__view_buyer',
      seller: 'tp-customer__view_seller'
    };
    if (state.role === constants.role.buyer) {
      body.classList.remove(className.seller);
      body.classList.add(className.buyer);
    } else {
      body.classList.remove(className.buyer);
      body.classList.add(className.seller);
    }
  };

  const initialize = () => {
    userService.authenticated().then((response) => {
      if (response) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user: helpers.getUser(),
            role: helpers.getRole(),
          }
        });
        setClassToBody();
      } else {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    });
  };

  const changeTheme = (theme) => {
    dispatch({
      type: 'CHANGETHEME',
      payload: {
        theme
      }
    });
  };

  const getExchangeRate = () => {
    commonService.exchangeRate().then((response) => {
      if (response) {
        setExchangeRates(response.filter((f) => f.name !== 'GBPTRY'));
      }
    });
  };
  const getFavSellerGroup = (filters) => {
    if (filters) {
      userService.getFavSellerGroupPost(filters).then((response) => {
        if (response) {
          setFavSellerGroup(response);
        }
      });
      return;
    }
    userService.getFavSellerGroup().then((response) => {
      if (response) {
        setFavSellerGroup(response);
      }
    });
  };

  useEffect(() => {
    const initializeEffect = initialize;
    initializeEffect();
  }, []);

  useEffect(() => {
    if (state.isAuthenticated) {
      getExchangeRate();
      getFavSellerGroup();
    }
  }, [state.isAuthenticated]);

  useEffect(() => {
    if (state.isAuthenticated) {
      setClassToBody();
    }
  }, [state.role]);

  const login = async (email, password) => {
    const result = {
      success: false,
      isFirstLogin: false,
    };
    try {
      const response = await userService.login({ email, password });
      if (response.token) {
        response.avatar = '';
        if (Array.isArray(response.sectorId)) {
          response.sectorId = helpers.nameToTitle(response.sectorId);
        }
        localStorage.setItem('user', JSON.stringify(response));
        helpers.setRole(response.role?.toLowerCase() === 'seller' ? constants.role.seller : constants.role.buyer);

        dispatch({
          type: 'LOGIN',
          payload: {
            user: response,
            role: helpers.getRole(),
          }
        });
        result.success = true;
        result.isFirstLogin = response.isFirstLogin;
        result.permissions = response.permissions;
      } else {
        toast.error(response);
        result.success = false;
      }
    } catch (error) {
      result.success = false;
      console.log(error.message);
    }
    return result;
  };

  const passwordReset = async (password, token) => {
    const response = await userService.passwordReset({ password, token });
    const { status } = response;
    if (status && (status === 422 || status === 500)) {
      const message = await response.text();
      return message;
    }
    return null;
  };
  const passwordRecovery = (email) => {
    userService.passwordRecovery(email)
      .then((response) => {
        response.avatar = '';
      }).catch((error) => {
        console.log(error);
      });
  };

  const logout = async () => {
    userService.logout()
      .then(() => {
      }).catch((error) => {
        console.log(error);
      });

    localStorage.removeItem('user');

    dispatch({ type: 'LOGOUT' });
  };

  const register = async (model) => {
    const response = await userService.register(model);
    const { status, data } = response;

    if (status && status === 422) {
      const message = await response.text();
      toast.error(message);
      return false;
    }

    if (status && status === 500) {
      const message = await response.text();
      toast.error(message);
      return false;
    }

    if (status && status === 200) {
      toast.success('Kayıt başarılı girişe yönlendiriliyorsunuz..');
      return data;
    }
    return response;
  };

  const setRole = async () => {
    dispatch({
      type: 'ROLE',
      payload: {
        role: helpers.getRole(),
      }
    });
  };

  const updateUser = () => {
    dispatch({
      type: 'INITIALIZE',
      payload: {
        isAuthenticated: true,
        user: helpers.getUser(),
        role: helpers.getRole(),
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        passwordRecovery,
        passwordReset,
        logout,
        register,
        setRole,
        initialize,
        updateUser,
        exchangeRates,
        changeTheme,
        favSellerGroup,
        getFavSellerGroup,
        contentSearchOpen,
        setContentSearchOpen,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
